import React, { useCallback } from 'react';

import { Element } from 'model/Form';
import Checkbox from 'components/Generic/Forms/FormFields/FormCheckbox';
import { EventData } from 'hooks/useDataLayer';
import useFormsFieldsSharedLogic from 'hooks/useFormFieldsSharedLogic';
import MultipleCheckboxes from 'components/Generic/Forms/FormFields/MultipleCheckboxes';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import InputField from 'components/Generic/Forms/FormFields/InputField';
import MultiplePickList from 'components/Generic/Forms/FormFields/MultiplePicklist';
import AbrdnSelect from 'components/Generic/Forms/FormFields/AbrdnSelect';
import DatePicker from 'components/Generic/Forms/FormFields/DatePicker';
import RadioButtonFieldset from 'components/Generic/Forms/FormFields/RadioButtonFieldset';
import Note from 'components/Generic/Forms/FormFields/Note';

export type FieldValue = {
  [key: string]: string | boolean | string[] | undefined;
  name: string;
  value: string | boolean | string[];
  relatesTo?: string;
};

type FormsSharedFieldsProps = {
  element: Element;
  formName?: string;
  formType: string;
  onRemoveAsset?: () => void;
  onChange: (value: FieldValue) => void;
  onUpdateDataLayer: (event: EventData) => void;
  onExecuteFormStartEvent: (event: EventData) => void;
};

const FormsSharedFields: React.FC<FormsSharedFieldsProps> = ({
  formName,
  formType,
  element,
  onRemoveAsset,
  onChange,
  onExecuteFormStartEvent,
  onUpdateDataLayer,
}) => {
  const {
    displayType,
    instructions,
    isRequired,
    name,
    htmlName,
    requiredErrorMessage,
    optionList,
    formattedFields,
    relatesTo,
    description,
  } = useFormsFieldsSharedLogic({
    element,
  });
  const fieldByFormType = formType === 'salesforce' ? 'id' : 'htmlName';
  const formField = fieldByFormType === 'id' ? element?.id : element?.htmlName;
  const getEventPayload = useCallback(
    (eventName: string) => ({
      event: eventName,
      form_field: formField,
      formName: formName,
    }),
    [formField, formName]
  );

  const onValueChange = useCallback(
    (value: string | boolean | string[]) => {
      if (onChange) {
        onChange({
          [fieldByFormType]: element?.[fieldByFormType],
          value,
          name: htmlName,
          relatesTo: relatesTo?.value,
        });
      }
    },

    [onChange, fieldByFormType, element, htmlName, relatesTo]
  );

  return (
    <>
      {displayType === 'checkbox' && (
        <div className="d-flex flex-column">
          {optionList?.elements ? (
            <MultipleCheckboxes
              required={isRequired}
              optionList={optionList.elements}
              requiredErrorMessage={requiredErrorMessage}
              instructions={instructions}
              label={formattedFields.label.value}
              onChange={onValueChange}
              onUpdateDataLayer={() => {
                onExecuteFormStartEvent(getEventPayload('form_start'));
                onUpdateDataLayer(getEventPayload('form_interaction'));
              }}
            />
          ) : (
            <Checkbox
              required={isRequired}
              onChange={onValueChange}
              onUpdateDataLayer={() => {
                onExecuteFormStartEvent(getEventPayload('form_start'));
                onUpdateDataLayer(getEventPayload('form_interaction'));
              }}
              slots={{
                label: <p className="body-small">{name}</p>,
                error: requiredErrorMessage,
                instructions,
              }}
            />
          )}
        </div>
      )}

      {displayType === 'singleSelect' && (
        <AbrdnSelect
          title={formattedFields.label.value}
          fields={formattedFields}
          id={formattedFields.id}
          options={
            formattedFields.options.value?.map((o) => ({
              value: o.value,
              label: o.displayName,
            })) ?? []
          }
          required={isRequired}
          placeholder={formattedFields.placeholder.value}
          onChange={(option) => {
            onValueChange(option.value);
            onUpdateDataLayer(getEventPayload('form_interaction'));
          }}
          onFocus={() => onExecuteFormStartEvent(getEventPayload('form_start'))}
        />
      )}

      {displayType === 'removeSubmission' && (
        <AbrdnButton buttonType="secondary" onClick={onRemoveAsset}>
          {name}
        </AbrdnButton>
      )}

      {['text', 'textArea', 'number'].includes(displayType) && (
        <InputField
          fields={formattedFields}
          multiline={displayType === 'textArea'}
          description={description}
          onFocus={() => onExecuteFormStartEvent(getEventPayload('form_start'))}
          onChange={(value: string) => {
            onValueChange(value);
            onUpdateDataLayer(getEventPayload('form_interaction'));
          }}
        />
      )}

      {displayType === 'radio' && (
        <RadioButtonFieldset
          fields={{
            ...element,
            optionList: { elements: element.optionList?.elements ?? [] },
            fieldDescription: element.description,
          }}
          onUpdateValue={(v) => {
            onValueChange(v);
            onUpdateDataLayer(getEventPayload('form_interaction'));
          }}
        />
      )}

      {displayType === 'note' && <Note fields={element} />}

      {displayType === 'datePicker' && <DatePicker fields={element} onChange={onValueChange} />}

      {displayType === 'multiSelect' && (
        <MultiplePickList
          fields={formattedFields}
          name={name}
          required={isRequired}
          optionList={optionList!}
          onUpdateDataLayer={() => onUpdateDataLayer(getEventPayload('form_interaction'))}
          onFocus={() => onExecuteFormStartEvent(getEventPayload('form_start'))}
          onChange={onValueChange}
        />
      )}
    </>
  );
};

export default FormsSharedFields;
